import type {
    SybilClearWalletResponse,
    SybilClusterClearResponse,
    SybilClusterClearWire,
    SybilWalletEvidence,
    SybilWalletEvidenceWire,
    SybilClearWalletWire,
    SybilClusterDetail,
    SybilClusterDetailWire,
    SybilClusterListResponse,
    SybilClusterListWire,
    SybilClusterStatus,
    SybilClusterSummary,
    SybilClusterWire,
    SybilWalletStatus,
    SybilWalletStatusWire,
} from '@/types/sybil'

// Maps blockpot-service's snake_case Sybil payloads (`internal/httpapi/sybil_handler.go`) to the
// console shapes. The service is the source of truth (BLO-937): nothing here invents a field
// the service does not send.

// `sybil.ThresholdReview` / `sybil.ThresholdBlock`.
const THRESHOLD_REVIEW = 3
const THRESHOLD_BLOCK = 5

export function mapSybilCluster(wire: SybilClusterWire): SybilClusterSummary {
    return {
        clusterId: wire.id,
        computedAt: wire.computed_at,
        correlator: wire.correlator,
        size: wire.size,
        walletAddresses: wire.wallets ?? [],
        isAnonymizing: wire.is_anonymizing,
    }
}

export function mapSybilClusterList(wire: SybilClusterListWire): SybilClusterListResponse {
    return {
        clusters: (wire.clusters ?? []).map(mapSybilCluster),
        nextCursor: wire.next_cursor || null,
    }
}

// Mirrors the service's `sybil.Status.ReviewState()`, the rule the pre-tx gate and the player
// detail `walletSybil.status` use: cleared wins, then blocked at the block threshold, then
// review between the two thresholds.
export function walletReviewState(
    wire: Pick<SybilWalletStatusWire, 'highest_cluster_size' | 'outcome'>,
): SybilClusterStatus | null {
    if (wire.outcome === 'cleared') return 'cleared'
    if (wire.highest_cluster_size >= THRESHOLD_BLOCK) return 'blocked'
    if (wire.highest_cluster_size >= THRESHOLD_REVIEW) return 'review'
    return null
}

// Mirrors the service's list `status` filter: cleared when every wallet is cleared, blocked
// when any uncleared wallet is at the block threshold, review when any is at review.
export function clusterReviewState(wallets: SybilWalletStatus[]): SybilClusterStatus | null {
    if (wallets.length > 0 && wallets.every((w) => w.outcome === 'cleared')) return 'cleared'
    if (wallets.some((w) => w.reviewState === 'blocked')) return 'blocked'
    if (wallets.some((w) => w.reviewState === 'review')) return 'review'
    return null
}

// The service only ever sends a /24 (IPv4) or /48 (IPv6) network. Anything finer, or a bare
// address, is dropped rather than shown: the console never renders a full IP (BLO-941).
export function coarseNetwork(value: string | null | undefined): string | null {
    if (!value) return null
    const m = value.match(/^(.+)\/(\d{1,3})$/)
    if (!m) return null
    const prefix = Number(m[2])
    const isV6 = m[1].includes(':')
    return prefix <= (isV6 ? 48 : 24) ? value : null
}

export function mapSybilWalletEvidence(wire: SybilWalletEvidenceWire): SybilWalletEvidence {
    return {
        correlator: wire.correlator,
        matchRef: wire.match_ref,
        network: coarseNetwork(wire.network),
        asn: wire.asn ?? null,
        isVpn: wire.is_vpn ?? null,
        isTor: wire.is_tor ?? null,
        isDatacenter: wire.is_datacenter ?? null,
        vasp: wire.vasp ?? null,
    }
}

export function mapSybilWalletStatus(wire: SybilWalletStatusWire): SybilWalletStatus {
    return {
        walletAddress: wire.wallet,
        highestClusterSize: wire.highest_cluster_size ?? 0,
        correlatorBreach: wire.correlator_breach ?? 0,
        outcome: wire.outcome ?? null,
        reviewState: walletReviewState(wire),
        reviewedAt: wire.reviewed_at ?? null,
        reviewedByAddress: wire.reviewed_by_address ?? null,
        clearReason: wire.clear_reason ?? null,
        clearNote: wire.clear_note ?? null,
        player: wire.player
            ? {
                  playerId: wire.player.player_id,
                  tier: wire.player.tier,
                  status: wire.player.status,
              }
            : null,
        caseId: wire.case_id ?? null,
        evidence: mapSybilWalletEvidence(wire.evidence),
    }
}

export function mapSybilClusterDetail(wire: SybilClusterDetailWire): SybilClusterDetail {
    const wallets = (wire.statuses ?? []).map(mapSybilWalletStatus)
    return {
        ...mapSybilCluster(wire),
        status: clusterReviewState(wallets),
        wallets,
    }
}

export function mapSybilClearResponse(wire: SybilClearWalletWire): SybilClearWalletResponse {
    return { walletAddress: wire.wallet, outcome: wire.outcome }
}

export function mapSybilClusterClearResponse(
    wire: SybilClusterClearWire,
): SybilClusterClearResponse {
    return {
        clusterId: wire.cluster_id,
        outcome: wire.outcome,
        reason: wire.reason,
        walletAddresses: wire.wallets ?? [],
    }
}
